import React, { createContext, useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import {
    getFirestore,
    collection,
    doc,
    onSnapshot,
    addDoc,
    setDoc,
    deleteDoc,
    DocumentReference
} from 'firebase/firestore';
import { useFirebase } from '../firebase/firebase-provider';
import { useSettings } from './settings-provider';
import { useAuth } from './auth-provider';
import { INVITATION_TYPE, useInvitations } from './invitations-provider';

interface HomeContextType {
    home: any | null;
    homeDb: DocumentReference | null | undefined;
    addHome: () => Promise<void>;
    addMember: (member: any, memberRoleId: any) => void;
    updateMemberRole: (member: any, memberRoleId: any) => void;
    removeMember: (memberId: any) => void;
    updateHome: (homeChange: any) => void;
    removeHome: () => void;
    acceptHomeInvitation: (invite: any) => Promise<void>;
    rejectHomeInvitation: (invite: any) => Promise<void>;
}

const HomeContext = createContext<HomeContextType | undefined>(undefined);

export const useHome = () => {
    const context = React.useContext(HomeContext);
    if (context === undefined) {
        throw new Error('useHome must be used within a HomeProvider');
    }
    return context;
};

const COLLECTION_HOMES = 'homes';
const COLLECTION_MEMBERS = 'members';

// export const SUB_ROLES = {
//     PLANNER_WRITE: 'PLANNER_WRITE',
// }

export const HOME_ROLES = {
    HEAD_CHEF: {
        id: 'HEAD_CHEF',
        name: 'Head Chef',
        description: 'Has complete control over the home. Can manage members, modify the meal planner and shopping lists',
    },
    SOUS_CHEF: {
        id: 'SOUS_CHEF',
        name: 'Sous Chef',
        description: 'Can plan meals and make shopping lists',
    },
    COOK: {
        id: 'COOK',
        name: 'Cook',
        description: 'Can view the planner, suggest meals and offer to cook',
    },
};

HomeProvider.propTypes = {
    children: PropTypes.element.isRequired,
};
export function HomeProvider(props: any) {
    const { privateSettings, setHomeId, getUsersById } = useSettings();
    const [home, setHome] = useState(null);
    const [homeDb, setHomeDb] = useState(undefined);
    const { currentUser } = useAuth();
    const { sendInvite, removeHomeInvitation } = useInvitations();
    const app = useFirebase();

    let homeId: any;
    if (privateSettings) {
        homeId = privateSettings.homeId ? privateSettings.homeId : null;
    }
    const db = getFirestore(app);
    const homeCollection = collection(db, COLLECTION_HOMES);

    useEffect(() => {
        if (homeId) {
            setHomeDb(doc(homeCollection, homeId));
        } else if (homeId === null) {
            setHomeDb(null);
        } else {
            setHomeDb(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homeId]);

    useEffect(() => {
        if (homeId) {
            setHomeDb(doc(homeCollection, homeId));
        } else if (homeId === null) {
            setHomeDb(null);
        } else {
            setHomeDb(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homeId]);

    useEffect(() => {
        if (homeDb) {
            const unsubscribe = onSnapshot(homeDb, (docSnap: any) => {
                if (docSnap.exists()) {
                    const homeData = docSnap.data();
                    const membersCollection = collection(homeDb, COLLECTION_MEMBERS);
                    onSnapshot(membersCollection, (querySnapshot: any) => {
                        const memberIds = querySnapshot.docs.map((m: any) => m.id);
                        if (!memberIds.includes(currentUser.uid)
                            || !querySnapshot.docs.find((m: any) => m.id === currentUser.uid).data().confirmed) {
                            setHomeId(null);
                            setHome({});
                        } else {
                            getUsersById(memberIds).then((homeMembers: any) => {
                                setHome({
                                    ...homeData,
                                    id: docSnap.id,
                                    members: homeMembers.map((member: any, index: any) => {
                                        const memberData = querySnapshot.docs[index].data();
                                        return ({
                                            ...member,
                                            role: HOME_ROLES[memberData.roleId],
                                            confirmed: !!memberData.confirmed,
                                        });
                                    }),
                                });
                            });
                        }
                    }, (error: any) => {
                        console.debug(error);
                        setHome({});
                    });
                } else {
                    setHome({});
                    setHomeId(null);
                }
            }, (error: any) => {
                console.debug(error);
                setHome({});
                // TODO: Let the user know something went wrong
            });
            return unsubscribe;
        } else if (homeDb === null) {
            setHome({});
        } else {
            setHome(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homeDb]);

    function addHome() {
        return new Promise<void>((resolve) => {
            addDoc(homeCollection, { ownerId: currentUser.uid })
                .then((docRef: any) => {
                    const memberDocRef = doc(collection(doc(homeCollection, docRef.id), COLLECTION_MEMBERS), currentUser.uid);
                    setDoc(memberDocRef, { id: currentUser.uid, roleId: HOME_ROLES.HEAD_CHEF.id, confirmed: true })
                        .then(() => {
                            setHomeId(docRef.id);
                            resolve();
                        });
                })
                .catch((err: any) => {
                    console.debug(err);
                    resolve();
                });
        });
    }

    function updateHome(homeChange: any) {
        if (homeDb) {
            setDoc(homeDb, homeChange, { merge: true }).then(() => {});
        }
    }

    function removeHome() {
        deleteDoc(homeDb).then(() => {
            setHomeId(null);
        });
    }

    function addMember(member: any, memberRoleId: any) {
        if (homeDb) {
            const memberDocRef = doc(collection(homeDb, COLLECTION_MEMBERS), member.id);
            setDoc(memberDocRef, { id: member.id, roleId: memberRoleId })
                .then(() => {
                    sendInvite(member.id, { type: INVITATION_TYPE.HOME, senderId: currentUser.uid, homeId })
                        .then(() => {})
                        .catch(console.debug);
                })
                .catch(console.debug);
        }
    }

    function acceptHomeInvitation(invite: any) {
        return new Promise<void>((resolve, reject) => {
            const memberDocRef = doc(collection(doc(homeCollection, invite.homeId), COLLECTION_MEMBERS), currentUser.uid);
            setDoc(memberDocRef, { confirmed: true }, { merge: true })
                .then(() => {
                    removeHomeInvitation(invite.id);
                    setHomeId(invite.homeId);
                    resolve();
                })
                .catch(reject);
        });
    }

    function rejectHomeInvitation(invite: any) {
        return new Promise<void>((resolve, reject) => {
            const memberDocRef = doc(collection(doc(homeCollection, invite.homeId), COLLECTION_MEMBERS), currentUser.uid);
            deleteDoc(memberDocRef)
                .then(() => {
                    removeHomeInvitation(invite.id);
                    setHomeId(null);
                    resolve();
                })
                .catch(reject);
        });
    }

    function updateMemberRole(member: any, memberRoleId: any) {
        if (homeDb) {
            const memberDocRef = doc(collection(homeDb, COLLECTION_MEMBERS), member.id);
            setDoc(memberDocRef, { roleId: memberRoleId }, { merge: true })
                .then(() => {});
        }
    }

    function removeMember(memberId: any) {
        if (homeDb) {
            const memberDocRef = doc(collection(homeDb, COLLECTION_MEMBERS), memberId);
            deleteDoc(memberDocRef)
                .then(() => {});
        }
    }

    return (
        <HomeContext.Provider value={{ home, homeDb, addHome, addMember, updateMemberRole, removeMember, updateHome, removeHome, acceptHomeInvitation, rejectHomeInvitation }}>
            { props.children }
        </HomeContext.Provider>
    );
}
